/**
 * @module features/clients/pages/ClientFormPage
 * @description Master full-page view for Client Onboarding and Editing.
 * Designed with a cognitive segmented tab layout to guarantee zero scrolling on desktop
 * ("no scroll with proper arranging for the sections in a page"), real-time white-label
 * brand portal preview, and touch-first mobile responsiveness (>=44px touch targets, >=16px font).
 */
import * as React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Building2,
  MapPin,
  UserCheck,
  Shield,
  ArrowLeft,
  Upload,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Copy,
  Check,
  KeyRound,
  Eye,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Drawer } from "@/components/ui/drawer";
import { ColorPickerInput } from "../components/ColorPickerInput";
import { ClientQuotaGauge } from "../components/ClientQuotaGauge";
import {
  useClientDetail,
  useOnboardClient,
  useUpdateClient,
  useUploadClientLogo,
  useDeleteClientLogo,
} from "../api/clientQueries";
import { ClientOnboardFormSchema, ClientOnboardFormValues } from "../types";
import { AppError } from "@/lib/api-client";

export interface ClientFormPageProps {
  mode: "create" | "edit";
}

type FormSectionId = "identity" | "address" | "contact" | "quota";

export function ClientFormPage({ mode }: ClientFormPageProps) {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();

  // Fetch client details if in edit mode
  const clientDetailQuery = useClientDetail(mode === "edit" ? clientId || null : null);
  const onboardMutation = useOnboardClient();
  const updateMutation = useUpdateClient();
  const uploadLogoMutation = useUploadClientLogo();
  const deleteLogoMutation = useDeleteClientLogo();

  // Active Segmented Section Tab
  const [activeTab, setActiveTab] = React.useState<FormSectionId>("identity");

  // Mobile Live Preview Drawer toggle
  const [mobilePreviewOpen, setMobilePreviewOpen] = React.useState(false);

  // Form Fields State
  const [orgName, setOrgName] = React.useState("");
  const [orgEmail, setOrgEmail] = React.useState("");
  const [orgContactNo, setOrgContactNo] = React.useState("");
  const [themeColor, setThemeColor] = React.useState("#2563EB");
  const [logoFile, setLogoFile] = React.useState<File | null>(null);
  const [logoPreview, setLogoPreview] = React.useState<string | null>(null);

  // Address
  const [street, setStreet] = React.useState("");
  const [landmark, setLandmark] = React.useState("");
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState("");
  const [country, setCountry] = React.useState("India");
  const [pincode, setPincode] = React.useState("");

  // Contact Person & Auto-provision
  const [contactName, setContactName] = React.useState("");
  const [contactEmail, setContactEmail] = React.useState("");
  const [contactMobile, setContactMobile] = React.useState("");
  const [autoProvision, setAutoProvision] = React.useState(false);

  // Quotas & Notifications
  const [maxUserLimit, setMaxUserLimit] = React.useState(10);
  const [notificationEmails, setNotificationEmails] = React.useState("");
  const [notificationMobiles, setNotificationMobiles] = React.useState("");

  // Feedback & Status
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [generatedCredentials, setGeneratedCredentials] = React.useState<{
    email: string;
    temporary_password: string;
  } | null>(null);
  const [copiedPassword, setCopiedPassword] = React.useState(false);
  const [copiedClientId, setCopiedClientId] = React.useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleCopyClientId = () => {
    if (!clientId) return;
    try {
      navigator.clipboard.writeText(clientId);
      setCopiedClientId(true);
      setTimeout(() => setCopiedClientId(false), 2000);
    } catch {
      // Ignore
    }
  };

  // Populate data in Edit Mode
  React.useEffect(() => {
    if (mode === "edit" && clientDetailQuery.data) {
      const c = clientDetailQuery.data;
      setOrgName(c.org_name || "");
      setOrgEmail(c.org_email_id || "");
      setOrgContactNo(c.org_contact_no || "");
      setThemeColor(c.theme_color || "#2563EB");
      setStreet(c.street || "");
      setLandmark(c.landmark || "");
      setCity(c.city || "");
      setState(c.state || "");
      setCountry(c.country || "India");
      setPincode(c.pincode || "");
      setContactName(c.contact_person_name || "");
      setContactEmail(c.contact_person_email || "");
      setContactMobile(c.contact_person_mobile_no || "");
      setMaxUserLimit(c.max_user_limit || 10);
      setNotificationEmails((c.notification_email_ids || []).join(", "));
      setNotificationMobiles((c.notification_mobile_nos || []).join(", "));
      setLogoPreview(c.logo_url || null);
    }
  }, [mode, clientDetailQuery.data]);

  // Handle Logo Upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setErrorMessage("Logo file size must not exceed 2MB.");
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = async () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (mode === "edit" && clientId && clientDetailQuery.data?.logo_url) {
      try {
        await deleteLogoMutation.mutateAsync(clientId);
      } catch (err) {
        console.warn("Failed to remove remote logo:", err);
      }
    }
  };

  // Section completion checks
  const isIdentityComplete = Boolean(orgName.trim() && orgEmail.trim() && orgContactNo.trim());
  const isAddressComplete = Boolean(street.trim() && city.trim() && state.trim() && pincode.trim());
  const isContactComplete = Boolean(contactName.trim() && contactEmail.trim() && contactMobile.trim());
  const isQuotaComplete = maxUserLimit >= 1;

  const sections: {
    id: FormSectionId;
    step: number;
    title: string;
    shortTitle: string;
    icon: React.ComponentType<{ className?: string }>;
    isComplete: boolean;
  }[] = [
    { id: "identity", step: 1, title: "Identity & Brand", shortTitle: "Identity", icon: Building2, isComplete: isIdentityComplete },
    { id: "address", step: 2, title: "Headquarters", shortTitle: "Location", icon: MapPin, isComplete: isAddressComplete },
    { id: "contact", step: 3, title: "Contact Liaison", shortTitle: "Contact", icon: UserCheck, isComplete: isContactComplete },
    { id: "quota", step: 4, title: "Quotas & Alerts", shortTitle: "Quotas", icon: Shield, isComplete: isQuotaComplete },
  ];

  const currentSectionIndex = sections.findIndex((s) => s.id === activeTab);

  // Form Submission
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setErrorMessage(null);
    setFieldErrors({});

    const formData: ClientOnboardFormValues = {
      org_name: orgName,
      org_email_id: orgEmail,
      org_contact_no: orgContactNo,
      theme_color: themeColor,
      street,
      landmark: landmark || undefined,
      city,
      state,
      country,
      pincode,
      contact_person_name: contactName,
      contact_person_email: contactEmail,
      contact_person_mobile_no: contactMobile,
      auto_provision_user: autoProvision,
      max_user_limit: maxUserLimit,
      notification_email_ids_raw: notificationEmails,
      notification_mobile_nos_raw: notificationMobiles,
      logo_url: "",
    };

    // Client-side Zod validation
    const validationResult = ClientOnboardFormSchema.safeParse(formData);
    if (!validationResult.success) {
      const errors: Record<string, string> = {};
      let firstErrorField = "";
      validationResult.error.issues.forEach((issue) => {
        const fieldKey = String(issue.path[0]);
        if (!errors[fieldKey]) errors[fieldKey] = issue.message;
        if (!firstErrorField) firstErrorField = fieldKey;
      });
      setFieldErrors(errors);

      // Automatically jump to the section containing the first error!
      if (["org_name", "org_email_id", "org_contact_no", "theme_color"].includes(firstErrorField)) {
        setActiveTab("identity");
      } else if (["street", "city", "state", "country", "pincode"].includes(firstErrorField)) {
        setActiveTab("address");
      } else if (["contact_person_name", "contact_person_email", "contact_person_mobile_no"].includes(firstErrorField)) {
        setActiveTab("contact");
      } else if (["max_user_limit"].includes(firstErrorField)) {
        setActiveTab("quota");
      }

      setErrorMessage("Please complete all required fields before saving.");
      return;
    }

    // Quota shrink guard in edit mode
    if (mode === "edit" && clientDetailQuery.data) {
      if (maxUserLimit < clientDetailQuery.data.active_user_count) {
        setActiveTab("quota");
        setErrorMessage(
          `Cannot reduce seat limit to ${maxUserLimit}. Organization currently has ${clientDetailQuery.data.active_user_count} active client accounts.`
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (mode === "create") {
        const response = await onboardMutation.mutateAsync(formData);

        // Upload logo if file attached
        if (logoFile && response.client?.id) {
          try {
            await uploadLogoMutation.mutateAsync({
              clientId: response.client.id,
              file: logoFile,
            });
          } catch (uploadErr) {
            console.warn("Logo upload failed after onboarding:", uploadErr);
          }
        }

        if (response.generated_credentials) {
          setGeneratedCredentials(response.generated_credentials);
        } else {
          navigate("/s-admin/clients");
        }
      } else if (mode === "edit" && clientId) {
        // 1. Upload logo if new file chosen
        if (logoFile) {
          try {
            await uploadLogoMutation.mutateAsync({ clientId, file: logoFile });
          } catch (uploadErr) {
            console.warn("Logo upload failed during edit:", uploadErr);
          }
        }

        // 2. Update client details
        await updateMutation.mutateAsync({
          clientId,
          data: formData,
        });

        navigate("/s-admin/clients");
      }
    } catch (rawError: unknown) {
      if (rawError instanceof AppError) {
        setErrorMessage(rawError.errorDetails || rawError.message);
      } else if (rawError instanceof Error) {
        setErrorMessage(rawError.message);
      } else {
        setErrorMessage("Failed to save client organization.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyPassword = () => {
    if (!generatedCredentials) return;
    try {
      navigator.clipboard.writeText(generatedCredentials.temporary_password);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    } catch {
      // Ignore
    }
  };

  // Loading skeleton in edit mode
  if (mode === "edit" && clientDetailQuery.isLoading) {
    return (
      <div className="space-y-6 animate-pulse" aria-busy="true">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-40 rounded-xl" />
        </div>
        <Skeleton className="h-10 w-full max-w-sm rounded-xl" />
        <Skeleton className="h-12 w-full rounded-2xl" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Skeleton className="lg:col-span-8 h-96 rounded-2xl" />
          <Skeleton className="lg:col-span-4 h-96 rounded-2xl" />
        </div>
      </div>
    );
  }

  // Error fallback in edit mode
  if (mode === "edit" && clientDetailQuery.isError) {
    return (
      <div className="p-8 rounded-2xl border border-destructive/20 bg-destructive/10 text-center space-y-4">
        <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
        <h3 className="text-base font-semibold text-foreground">Failed to load organization</h3>
        <p className="text-xs text-muted-foreground max-w-sm mx-auto">
          The requested client record could not be loaded from the server.
        </p>
        <Button variant="outline" size="sm" onClick={() => clientDetailQuery.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  // Render the Live Interactive White-Label Preview Card
  const renderLivePreviewCard = (isModal = false) => (
    <div
      className={`rounded-2xl border border-border/70 dark:border-zinc-800/80 bg-card dark:bg-zinc-950 p-4 sm:p-5 shadow-xs space-y-4 ${
        isModal ? "" : "sticky top-4"
      }`}
    >
      <div className="flex items-center justify-between pb-3 border-b border-border/60">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-foreground/70" />
          <span className="text-xs font-bold text-foreground">Live White-Label Preview</span>
        </div>
        <span
          className="text-[10px] font-mono px-2 py-0.5 rounded-full ring-1 ring-border shadow-xs text-white"
          style={{ backgroundColor: themeColor }}
        >
          {themeColor}
        </span>
      </div>

      {/* Mock Client Portal Navbar */}
      <div className="rounded-xl overflow-hidden border border-border/80 shadow-xs">
        <div
          className="p-3.5 flex items-center justify-between text-white transition-colors duration-200"
          style={{ backgroundColor: themeColor }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Logo"
                className="h-7 w-7 rounded-lg object-contain bg-white p-0.5 shrink-0 shadow-xs"
              />
            ) : (
              <div className="h-7 w-7 rounded-lg bg-black/20 backdrop-blur-xs flex items-center justify-center font-bold text-xs shrink-0 shadow-xs text-white">
                {(orgName || "RX").substring(0, 2).toUpperCase()}
              </div>
            )}
            <span className="font-bold text-xs truncate drop-shadow-xs">
              {orgName || "Organization Name"}
            </span>
          </div>

          <span className="text-[10px] bg-black/20 backdrop-blur-xs px-2 py-0.5 rounded-full font-mono text-white/90 shrink-0">
            Client Portal
          </span>
        </div>

        {/* Mock Portal Body Snippet */}
        <div className="p-3 bg-muted/20 text-xs space-y-2">
          {mode === "edit" && clientId && (
            <div className="flex items-center justify-between text-[11px] text-muted-foreground font-mono pb-1 border-b border-border/40">
              <span>Client ID:</span>
              <button
                type="button"
                onClick={handleCopyClientId}
                className="font-mono font-semibold text-foreground hover:text-blue-600 transition-colors inline-flex items-center gap-1 select-all"
                title="Click to copy ID"
              >
                <span className="truncate max-w-[150px]">{clientId}</span>
                {copiedClientId ? (
                  <Check className="h-3 w-3 text-emerald-500 shrink-0" />
                ) : (
                  <Copy className="h-3 w-3 opacity-60 shrink-0" />
                )}
              </button>
            </div>
          )}
          <div className="flex items-center justify-between text-[11px] text-muted-foreground font-mono">
            <span>Official Email:</span>
            <span className="text-foreground truncate max-w-[160px]">
              {orgEmail || "contact@org.com"}
            </span>
          </div>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground font-mono">
            <span>Location:</span>
            <span className="text-foreground truncate max-w-[160px]">
              {city && state ? `${city}, ${state}` : "City, State"}
            </span>
          </div>
        </div>
      </div>

      {/* Live Seat Quota Meter */}
      <ClientQuotaGauge
        activeCount={mode === "edit" ? clientDetailQuery.data?.active_user_count || 0 : 0}
        maxLimit={maxUserLimit}
      />

      {/* Liaison Card */}
      <div className="p-3 rounded-xl bg-muted/30 border border-border/50 text-xs space-y-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Assigned Contact Liaison
        </p>
        <p className="font-semibold text-foreground truncate">
          {contactName || "Contact Person"}
        </p>
        <p className="text-[11px] font-mono text-muted-foreground truncate">
          {contactEmail || "person@org.com"} • {contactMobile || "+91-XXXXXXXXXX"}
        </p>
      </div>

      {/* Quick Section Jump Navigator */}
      <div className="pt-2 border-t border-border/50">
        <p className="text-[11px] font-semibold text-muted-foreground mb-2">Sections Checklist</p>
        <div className="space-y-1.5">
          {sections.map((sec) => {
            const Icon = sec.icon;
            const isCurrent = activeTab === sec.id;
            return (
              <button
                key={sec.id}
                type="button"
                onClick={() => {
                  setActiveTab(sec.id);
                  if (isModal) setMobilePreviewOpen(false);
                }}
                className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-colors text-left ${
                  isCurrent
                    ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 font-semibold"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                <div className="flex items-center gap-2 truncate">
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{sec.title}</span>
                </div>
                {sec.isComplete ? (
                  <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                ) : (
                  <span className="text-[10px] text-muted-foreground opacity-60">Required</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-5 max-w-7xl mx-auto">
      {/* Streamlined Modern Header with Copyable Client ID */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-border/60">
        <div className="flex items-center gap-2.5 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground gap-1.5"
          >
            <Link to="/s-admin/clients">
              <ArrowLeft className="h-3.5 w-3.5" />
              <span>Clients</span>
            </Link>
          </Button>
          <span className="text-muted-foreground/40 text-xs">•</span>
          <h1 className="text-sm font-semibold text-foreground tracking-tight">
            {mode === "create" ? "Onboard Client Organization" : `Edit ${orgName || "Organization"}`}
          </h1>

          {/* Prominent Client ID Badge in Edit Mode */}
          {mode === "edit" && clientId && (
            <button
              type="button"
              onClick={handleCopyClientId}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted/70 hover:bg-muted text-xs font-mono text-muted-foreground hover:text-foreground border border-border/80 transition-colors group select-all shadow-2xs"
              title="Click to copy Client Organization ID"
            >
              <span className="font-semibold text-foreground">ID:</span>
              <span className="tracking-tight">{clientId}</span>
              {copiedClientId ? (
                <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
              ) : (
                <Copy className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100 shrink-0" />
              )}
            </button>
          )}
        </div>

        {/* Header Action CTAs */}
        <div className="flex items-center gap-2 justify-between sm:justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setMobilePreviewOpen(true)}
            className="h-8 px-2.5 text-xs lg:hidden gap-1.5 text-muted-foreground"
          >
            <Eye className="h-3.5 w-3.5" />
            <span>Brand Preview</span>
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate("/s-admin/clients")}
            disabled={isSubmitting}
            className="h-8 px-2.5 text-xs text-muted-foreground hover:text-foreground hidden sm:inline-flex"
          >
            Cancel
          </Button>

          <Button
            type="button"
            size="sm"
            disabled={isSubmitting}
            onClick={() => void handleSubmit()}
            className="h-8 px-3 text-xs shadow-xs bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white gap-1.5 font-medium"
          >
            {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            <span>{mode === "create" ? "Onboard Client" : "Save Changes"}</span>
          </Button>
        </div>
      </div>

      {/* Global Error Banner */}
      {errorMessage && (
        <div
          role="alert"
          className="p-3.5 sm:p-4 rounded-xl border border-destructive/20 bg-destructive/10 text-xs text-destructive flex items-start gap-3 shadow-xs animate-in fade-in-50"
        >
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <p className="font-semibold text-xs">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Success Modal: Auto-Provisioned Credentials */}
      {generatedCredentials && (
        <div className="p-5 sm:p-6 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 shadow-md space-y-4 animate-in zoom-in-95">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-emerald-500 text-white shadow-xs">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-base font-bold text-emerald-950 dark:text-emerald-100">
                Organization Onboarded Successfully!
              </h3>
              <p className="text-xs text-emerald-700 dark:text-emerald-300">
                A client administrator account was auto-provisioned with the credentials below.
              </p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-white dark:bg-zinc-900 border border-emerald-200 dark:border-emerald-800/60 space-y-2.5">
            <div className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground font-medium">Portal Email:</span>
              <span className="font-mono font-semibold text-foreground">
                {generatedCredentials.email}
              </span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground font-medium">Temporary Password:</span>
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/80 px-2 py-1 rounded-md border border-emerald-200 dark:border-emerald-800">
                  {generatedCredentials.temporary_password}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopyPassword}
                  className="h-7 px-2 text-xs gap-1"
                >
                  {copiedPassword ? (
                    <Check className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                  <span>{copiedPassword ? "Copied" : "Copy"}</span>
                </Button>
              </div>
            </div>
          </div>

          <p className="text-[11px] text-emerald-800 dark:text-emerald-300">
            Please share this temporary password securely with the authorized liaison. They will be prompted to set a new password on their first sign in.
          </p>

          <Button
            size="sm"
            onClick={() => navigate("/s-admin/clients")}
            className="w-full sm:w-auto text-xs"
          >
            Done & Return to Client Directory
          </Button>
        </div>
      )}

      {/* Main Form & Live Preview Layout */}
      {!generatedCredentials && (
        <div className="space-y-4">
          {/* Segmented Section Stepper Tabs (Pills) */}
          <div className="grid grid-cols-4 p-1 bg-muted/60 dark:bg-zinc-900 rounded-xl border border-border/60 dark:border-zinc-800 gap-1">
            {sections.map((sec) => {
              const Icon = sec.icon;
              const isActive = activeTab === sec.id;
              return (
                <button
                  key={sec.id}
                  type="button"
                  onClick={() => setActiveTab(sec.id)}
                  className={`flex items-center justify-center gap-1.5 px-2 sm:px-4 py-2 text-xs font-semibold rounded-lg transition-all duration-150 min-h-[40px] ${
                    isActive
                      ? "bg-card dark:bg-zinc-800 text-foreground shadow-xs border border-border/40 dark:border-zinc-700"
                      : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0 hidden sm:inline-block" />
                  <span className="hidden sm:inline">
                    {sec.step}. {sec.title}
                  </span>
                  <span className="inline sm:hidden truncate text-[11px]">
                    {sec.step}. {sec.shortTitle}
                  </span>
                  {sec.isComplete ? (
                    <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/30 shrink-0 hidden xs:inline-block" />
                  )}
                </button>
              );
            })}
          </div>

          {/* 12-Column Responsive Canvas */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 items-start">
            {/* Left 8 Columns: Active Section Form Card (NO SCROLL ON DESKTOP) */}
            <div className="lg:col-span-8 rounded-2xl border border-border/70 dark:border-zinc-800/80 bg-card dark:bg-zinc-950 p-4 sm:p-6 shadow-xs flex flex-col justify-between min-h-[440px]">
              <div>
                {/* ----------------------------------------------------------------- */}
                {/* Section 1: Organization Identity & Brand                          */}
                {/* ----------------------------------------------------------------- */}
                {activeTab === "identity" && (
                  <div className="space-y-4 animate-in fade-in-50 duration-150">
                    <div className="pb-3 border-b border-border/50">
                      <h3 className="font-bold text-sm text-foreground flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-foreground/80" />
                        Organization Identity & Brand Styling
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Legal organization profile and dynamic white-label portal color accent
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="text-xs font-semibold text-foreground">
                          Organization Legal Name *
                        </label>
                        <Input
                          value={orgName}
                          onChange={(e) => setOrgName(e.target.value)}
                          placeholder="e.g. Acme Corporation Ltd"
                          className="mt-1 h-10 text-base sm:text-xs"
                          aria-invalid={Boolean(fieldErrors.org_name)}
                        />
                        {fieldErrors.org_name && (
                          <p className="text-xs text-red-500 mt-1">{fieldErrors.org_name}</p>
                        )}
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-foreground">
                          Official Organization Email *
                        </label>
                        <Input
                          type="email"
                          value={orgEmail}
                          onChange={(e) => setOrgEmail(e.target.value)}
                          placeholder="admin@acme.com"
                          className="mt-1 h-10 text-base sm:text-xs"
                          aria-invalid={Boolean(fieldErrors.org_email_id)}
                        />
                        {fieldErrors.org_email_id && (
                          <p className="text-xs text-red-500 mt-1">{fieldErrors.org_email_id}</p>
                        )}
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-foreground">
                          Organization Contact Phone *
                        </label>
                        <Input
                          value={orgContactNo}
                          onChange={(e) => setOrgContactNo(e.target.value)}
                          placeholder="+91-9876543210"
                          className="mt-1 h-10 text-base sm:text-xs"
                          aria-invalid={Boolean(fieldErrors.org_contact_no)}
                        />
                        {fieldErrors.org_contact_no && (
                          <p className="text-xs text-red-500 mt-1">{fieldErrors.org_contact_no}</p>
                        )}
                      </div>
                    </div>

                    {/* Logo & Theme Row */}
                    <div className="pt-3 border-t border-border/50 grid grid-cols-1 sm:grid-cols-2 gap-5 items-start">
                      {/* Logo Upload */}
                      <div>
                        <label className="text-xs font-semibold text-foreground block mb-1">
                          Organization Logo (Optional)
                        </label>
                        <div className="flex items-center gap-3 mt-1.5">
                          {logoPreview ? (
                            <div className="relative group">
                              <img
                                src={logoPreview}
                                alt="Logo Preview"
                                className="h-12 w-12 rounded-xl object-contain border border-border/80 bg-white p-1 shadow-xs"
                              />
                              <button
                                type="button"
                                onClick={handleRemoveLogo}
                                className="absolute -top-1.5 -right-1.5 p-1 rounded-full bg-destructive text-white shadow-xs hover:bg-destructive/90 transition-colors"
                                title="Remove Logo"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <div className="h-12 w-12 rounded-xl border border-dashed border-border flex items-center justify-center text-muted-foreground bg-muted/20">
                              <Building2 className="h-5 w-5" />
                            </div>
                          )}

                          <div>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/png,image/jpeg,image/webp,image/svg+xml"
                              onChange={handleFileChange}
                              className="hidden"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              className="h-8 text-xs gap-1.5"
                            >
                              <Upload className="h-3.5 w-3.5" />
                              <span>{logoPreview ? "Change Logo" : "Upload Logo"}</span>
                            </Button>
                            <p className="text-[11px] text-muted-foreground mt-1">
                              PNG, JPG, or SVG (&le; 2MB).
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Theme Color Picker */}
                      <div>
                        <label className="text-xs font-semibold text-foreground block mb-1">
                          Client Portal Accent Color *
                        </label>
                        <ColorPickerInput
                          value={themeColor}
                          onChange={setThemeColor}
                          error={fieldErrors.theme_color}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ----------------------------------------------------------------- */}
                {/* Section 2: Physical Headquarters Address                          */}
                {/* ----------------------------------------------------------------- */}
                {activeTab === "address" && (
                  <div className="space-y-4 animate-in fade-in-50 duration-150">
                    <div className="pb-3 border-b border-border/50">
                      <h3 className="font-bold text-sm text-foreground flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-foreground/80" />
                        Physical Headquarters Address
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Registered corporate physical location and jurisdiction details
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="text-xs font-semibold text-foreground">
                          Street Address *
                        </label>
                        <Input
                          value={street}
                          onChange={(e) => setStreet(e.target.value)}
                          placeholder="e.g. 104, Tech Boulevard, Sector 5"
                          className="mt-1 h-10 text-base sm:text-xs"
                          aria-invalid={Boolean(fieldErrors.street)}
                        />
                        {fieldErrors.street && (
                          <p className="text-xs text-red-500 mt-1">{fieldErrors.street}</p>
                        )}
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-xs font-semibold text-foreground">
                          Landmark (Optional)
                        </label>
                        <Input
                          value={landmark}
                          onChange={(e) => setLandmark(e.target.value)}
                          placeholder="Near Metro Station"
                          className="mt-1 h-10 text-base sm:text-xs"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-foreground">City *</label>
                        <Input
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          placeholder="e.g. Bengaluru"
                          className="mt-1 h-10 text-base sm:text-xs"
                          aria-invalid={Boolean(fieldErrors.city)}
                        />
                        {fieldErrors.city && (
                          <p className="text-xs text-red-500 mt-1">{fieldErrors.city}</p>
                        )}
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-foreground">
                          State / Province *
                        </label>
                        <Input
                          value={state}
                          onChange={(e) => setState(e.target.value)}
                          placeholder="e.g. Karnataka"
                          className="mt-1 h-10 text-base sm:text-xs"
                          aria-invalid={Boolean(fieldErrors.state)}
                        />
                        {fieldErrors.state && (
                          <p className="text-xs text-red-500 mt-1">{fieldErrors.state}</p>
                        )}
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-foreground">Country *</label>
                        <Input
                          value={country}
                          onChange={(e) => setCountry(e.target.value)}
                          placeholder="India"
                          className="mt-1 h-10 text-base sm:text-xs"
                          aria-invalid={Boolean(fieldErrors.country)}
                        />
                        {fieldErrors.country && (
                          <p className="text-xs text-red-500 mt-1">{fieldErrors.country}</p>
                        )}
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-foreground">
                          Postal Pincode *
                        </label>
                        <Input
                          value={pincode}
                          onChange={(e) => setPincode(e.target.value)}
                          placeholder="e.g. 560100"
                          className="mt-1 h-10 text-base sm:text-xs"
                          aria-invalid={Boolean(fieldErrors.pincode)}
                        />
                        {fieldErrors.pincode && (
                          <p className="text-xs text-red-500 mt-1">{fieldErrors.pincode}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ----------------------------------------------------------------- */}
                {/* Section 3: Primary Contact Person                                 */}
                {/* ----------------------------------------------------------------- */}
                {activeTab === "contact" && (
                  <div className="space-y-4 animate-in fade-in-50 duration-150">
                    <div className="pb-3 border-b border-border/50">
                      <h3 className="font-bold text-sm text-foreground flex items-center gap-2">
                        <UserCheck className="h-4 w-4 text-foreground/80" />
                        Primary Administrative Liaison
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Authorized executive contact responsible for client organization supervision
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="text-xs font-semibold text-foreground">
                          Contact Person Full Name *
                        </label>
                        <Input
                          value={contactName}
                          onChange={(e) => setContactName(e.target.value)}
                          placeholder="e.g. Johnathan Doe"
                          className="mt-1 h-10 text-base sm:text-xs"
                          aria-invalid={Boolean(fieldErrors.contact_person_name)}
                        />
                        {fieldErrors.contact_person_name && (
                          <p className="text-xs text-red-500 mt-1">
                            {fieldErrors.contact_person_name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-foreground">
                          Contact Official Email *
                        </label>
                        <Input
                          type="email"
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                          placeholder="john.doe@acme.com"
                          className="mt-1 h-10 text-base sm:text-xs"
                          aria-invalid={Boolean(fieldErrors.contact_person_email)}
                        />
                        {fieldErrors.contact_person_email && (
                          <p className="text-xs text-red-500 mt-1">
                            {fieldErrors.contact_person_email}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-foreground">
                          Contact Direct Mobile *
                        </label>
                        <Input
                          value={contactMobile}
                          onChange={(e) => setContactMobile(e.target.value)}
                          placeholder="+91-9123456789"
                          className="mt-1 h-10 text-base sm:text-xs"
                          aria-invalid={Boolean(fieldErrors.contact_person_mobile_no)}
                        />
                        {fieldErrors.contact_person_mobile_no && (
                          <p className="text-xs text-red-500 mt-1">
                            {fieldErrors.contact_person_mobile_no}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Auto-provision toggle (create mode only) */}
                    {mode === "create" && (
                      <div className="pt-3 border-t border-border/50">
                        <div className="flex items-start gap-3 p-3.5 rounded-xl bg-purple-500/10 border border-purple-500/20">
                          <Checkbox
                            id="auto-provision-cb"
                            checked={autoProvision}
                            onCheckedChange={(c) => setAutoProvision(Boolean(c))}
                            className="mt-0.5"
                          />
                          <label
                            htmlFor="auto-provision-cb"
                            className="text-xs cursor-pointer select-none space-y-0.5"
                          >
                            <p className="font-semibold text-foreground flex items-center gap-1.5">
                              <KeyRound className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                              Auto-provision client portal user account for contact person
                            </p>
                            <p className="text-muted-foreground text-[11px]">
                              Creates a linked client account and generates a one-time temporary password.
                            </p>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ----------------------------------------------------------------- */}
                {/* Section 4: Quotas & Operational Notifications                     */}
                {/* ----------------------------------------------------------------- */}
                {activeTab === "quota" && (
                  <div className="space-y-4 animate-in fade-in-50 duration-150">
                    <div className="pb-3 border-b border-border/50">
                      <h3 className="font-bold text-sm text-foreground flex items-center gap-2">
                        <Shield className="h-4 w-4 text-foreground/80" />
                        Seat Quota Capacity & Operational Alerts
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Enforce multi-tenant resource boundaries and register platform alert targets
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="text-xs font-semibold text-foreground">
                          Maximum User Seat Limit *
                        </label>
                        <Input
                          type="number"
                          min={mode === "edit" ? clientDetailQuery.data?.active_user_count || 1 : 1}
                          value={maxUserLimit}
                          onChange={(e) => setMaxUserLimit(parseInt(e.target.value, 10) || 1)}
                          className="mt-1 h-10 text-base sm:text-xs max-w-xs"
                          aria-invalid={Boolean(fieldErrors.max_user_limit)}
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Enforces capacity ceiling. Cannot be reduced below active linked users.
                        </p>
                        {fieldErrors.max_user_limit && (
                          <p className="text-xs text-red-500 mt-1">{fieldErrors.max_user_limit}</p>
                        )}
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-foreground">
                          Notification Email Recipients (Optional)
                        </label>
                        <Input
                          value={notificationEmails}
                          onChange={(e) => setNotificationEmails(e.target.value)}
                          placeholder="alerts@acme.com, noc@acme.com"
                          className="mt-1 h-10 text-base sm:text-xs"
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Comma-separated emails to receive platform maintenance broadcasts.
                        </p>
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-foreground">
                          Notification Mobile Numbers (Optional)
                        </label>
                        <Input
                          value={notificationMobiles}
                          onChange={(e) => setNotificationMobiles(e.target.value)}
                          placeholder="+91-9876543210, +91-9988776655"
                          className="mt-1 h-10 text-base sm:text-xs"
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Comma-separated phone numbers for SMS emergency alerts.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom Step Navigation Bar */}
              <div className="pt-4 border-t border-border/50 flex items-center justify-between gap-2.5 mt-5">
                <div>
                  {currentSectionIndex > 0 ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setActiveTab(sections[currentSectionIndex - 1].id)}
                      className="h-10 sm:h-9 text-xs gap-1.5 px-3"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span className="hidden sm:inline">Previous: {sections[currentSectionIndex - 1].shortTitle}</span>
                      <span className="inline sm:hidden">Back</span>
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/s-admin/clients")}
                      className="h-10 sm:h-9 text-xs px-3"
                    >
                      Cancel
                    </Button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {currentSectionIndex < sections.length - 1 ? (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setActiveTab(sections[currentSectionIndex + 1].id)}
                      className="h-10 sm:h-9 text-xs gap-1.5 shadow-xs bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-950 px-3.5 sm:px-4 font-medium"
                    >
                      <span className="hidden sm:inline">Next: {sections[currentSectionIndex + 1].shortTitle}</span>
                      <span className="inline sm:hidden">Next Step</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      disabled={isSubmitting}
                      onClick={() => void handleSubmit()}
                      className="h-10 sm:h-9 text-xs gap-1.5 shadow-xs bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 px-3.5 sm:px-4 font-medium"
                    >
                      {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                      <span>{mode === "create" ? "Onboard Client" : "Save Changes"}</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Right 4 Columns: Live White-Label Brand & Portal Preview Card (Desktop) */}
            <div className="hidden lg:block lg:col-span-4">
              {renderLivePreviewCard(false)}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Live Preview Drawer Sheet */}
      <Drawer
        open={mobilePreviewOpen}
        onClose={() => setMobilePreviewOpen(false)}
        title="Live Brand Preview"
        className="sm:hidden"
      >
        <div className="p-4">{renderLivePreviewCard(true)}</div>
      </Drawer>
    </div>
  );
}
